import * as React from 'react';
import { __ } from 'react-i18n/lib';
import styled from 'styled-components';
import { rem, rgba } from 'polished';
import { DsiSearchResultViewer as SearchResultsViewer } from './SearchResultViewer';
import { SearchFormInput } from './SearchFormInput';
import { Button } from '../../Dsi/General/Button';
import { OutsideClick } from '../../Defaults/OutsideClick';
import {
  IconType,
  DsiSvgIcon as SvgIcon,
  DsiSvgIcon,
} from '../../Dsi/General/SvgIcon';

// Interfaces
interface SearchFormProps {
  searchResults: {
    productsResult: any;
    categoriesResult: any;
    articlesResult: any;
  };
  searchTerm: string;
  redirectToSearchResultPage: (e) => void;
  handleSearchTermChange: (e) => void;
  handleFocus: () => void;
  resultsDropdownVisible: boolean;
  hideResultViewer: () => void;
  isMobile?: boolean;
  partialSearchProductsIsFetching: boolean;
  partialSearchCategoriesIsFetching: boolean;
  buttonHasArrow?: boolean;
  clearSearchInput: () => void;
  partialSearchArticlesIsFetching: boolean;
  showMoc: boolean;
  showWithVat: boolean;
}

/*****************/

const SearchFormWrapper = styled.form`
  width: 100%;
  margin-left: ${rem(24)};

  ${({ theme }) => theme.mediab.l1150`
    margin: 0 auto;
    max-width: ${rem(700)};
  `}
`;

const MobileSearchIcon = styled(Button)`
  display: none;
  background: transparent;
  padding: 0;
  position: absolute;
  z-index: 502;
  left: ${rem(16)};
  top: ${rem(8)};
  width: ${rem(24)};
  height: ${rem(24)};

  ${({ theme }) => theme.mediab.l1150`
    display: block;
  `}
  svg {
    fill: ${({ theme }) => theme.colors.black};
  }
`;

const SearchFormContainer = styled.div`
  display: flex;
  position: relative;
`;

const SearchFormInputWrapper = styled.div`
  flex: 1 0 auto;
  /* width: 100%; */
  position: relative;
`;

const SearchFormSubmit = styled(Button)`
  border-radius: 0 ${rem(4)} ${rem(4)} 0;
  transition: filter 0.2s ease;
  padding: ${rem(12)} ${rem(12)};
  height: ${rem(56)};
  width: ${rem(56)};

  &:hover {
    filter: brightness(90%);
  }

  ${({ theme }) => theme.media(0, 'sm')} {
    display: none;
  }
`;

const StyledSearchFormInput = styled(SearchFormInput)`
  padding-left: ${rem(16)};
  border-radius: ${rem(4)} 0 0 ${rem(4)};
  box-shadow: ${({ theme }) => theme.searchFormInput.boxShadow};
  border: ${({ theme }) => theme.searchFormInput.border};
  height: ${rem(56)};
  position: relative;
  z-index: 501;
  font-size: ${rem(14)};

  ${({ theme }) => theme.mediab.l1150`
    padding-left: ${rem(56)};
    border-radius: 0;
    border: none;
    box-shadow:none;
  `}

  ${props =>
    props.resultsDropdownVisible
      ? `border-radius: ${rem(4)} 0 0 ${rem(0)}; 
      border-bottom: 0;
      box-shadow: none;`
      : ''}
`;

const ClearSearch = styled.button<{ isShown: boolean }>`
  border: none;
  box-shadow: none;
  background: ${({ theme }) => rgba(theme.colors.primary, 0.04)};
  position: absolute;
  right: ${rem(16)};
  top: ${rem(16)};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${rem(24)};
  height: ${rem(24)};
  padding: 0;
  cursor: pointer;
  z-index: 502;
  display: ${props => (props.isShown ? 'flex' : 'none')};

  svg {
    fill: ${({ theme }) => theme.colors.primary};
  }

  ${({ theme }) => theme.mediab.l1150`
    display: flex !important;
  `}
`;

export const SearchFormIcon: any = styled.img`
  fill: #cccccc;
  position: absolute;
  top: 50%;
  left: ${rem(15)};
  transform: translate3d(0, -50%, 0);
  width: ${rem(24)};
  height: ${rem(24)};
`;

function SearchForm({
  searchResults,
  redirectToSearchResultPage,
  searchTerm,
  handleFocus,
  handleSearchTermChange,
  resultsDropdownVisible,
  hideResultViewer,
  isMobile = false,
  partialSearchProductsIsFetching,
  partialSearchCategoriesIsFetching,
  buttonHasArrow = false,
  clearSearchInput,
  partialSearchArticlesIsFetching,
  showMoc,
  showWithVat,
}: SearchFormProps) {
  const handleClear = (e: any) => {
    clearSearchInput();
  };

  return (
    <SearchFormWrapper autoComplete="off" onSubmit={redirectToSearchResultPage}>
      <OutsideClick handleFunction={hideResultViewer}>
        <SearchFormContainer>
          <SearchFormInputWrapper>
            <MobileSearchIcon>
              <SvgIcon
                icon={IconType.search}
                alt={__('Hľadať')}
                cursor={'pointer'}
                // tslint:disable-next-line:jsx-no-lambda
              />
            </MobileSearchIcon>
            <StyledSearchFormInput
              isMobile={isMobile}
              name="q"
              type="text"
              placeholder={__('Hľadaný výraz...')}
              value={searchTerm}
              onFocus={handleFocus}
              onChange={handleSearchTermChange}
              resultsDropdownVisible={resultsDropdownVisible}
            />
            <ClearSearch
              isShown={Boolean(searchTerm && searchTerm.length > 0)}
              type="button"
              onClick={handleClear}
              title={__('Vyčistiť text')}
            >
              <DsiSvgIcon
                icon={IconType.x}
                width={12}
                height={12}
                viewBox="1 1 10 10"
                cursor="pointer"
                alt={__('Vyčistiť text')}
              />
            </ClearSearch>
            <SearchResultsViewer
              partialSearchArticlesIsFetching={partialSearchArticlesIsFetching}
              partialSearchProductsIsFetching={partialSearchProductsIsFetching}
              partialSearchCategoriesIsFetching={
                partialSearchCategoriesIsFetching
              }
              visible={resultsDropdownVisible}
              searchResults={searchResults}
              searchTerm={searchTerm}
              redirectToResultPage={redirectToSearchResultPage}
              hideResultViewer={hideResultViewer}
              buttonHasArrow={buttonHasArrow}
              showMoc={showMoc}
              showWithVat={showWithVat}
            />
          </SearchFormInputWrapper>
          {!isMobile && (
            <SearchFormSubmit
              type="submit"
              onClick={redirectToSearchResultPage}
            >
              <SvgIcon icon={IconType.search} fill={'#FFFFFF'} />
            </SearchFormSubmit>
          )}
        </SearchFormContainer>
      </OutsideClick>
    </SearchFormWrapper>
  );
}

export { SearchForm as DsiSearchForm };
